import { forwardRef, Ref, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import { LayoutChangeEvent, StyleSheet, View, ViewProps } from 'react-native'
import { Defs, G, Line, Marker, Svg, Path } from 'react-native-svg'
import { animationFrameScheduler, fromEvent, Subscription } from 'rxjs'
import { concatMap, filter, map, subscribeOn, switchMap, takeUntil, tap } from 'rxjs/operators'
import { scaleOfElement } from 'zens-positionable'

import { Color } from '../../style'

import { Position } from './positionable'

const STATUS = ['start', 'end', 'edit']
const ARROW_WIDTH = 8
const ARROW_HEIGHT = 6
const ENDPOINT_HANDLE = 35

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  add: {
    // @ts-expect-error Cursor only relevant for web
    cursor: 'crosshair',
  },
  endpoint: {
    cursor: 'pointer',
    position: 'absolute',
  },
})

export interface ScalerRef {
  reset: () => void
}

export interface Props extends ViewProps {
  value?: Position[]
  onChange?: (value: Position[]) => void
  onComplete?: (value: Position[]) => void
  scale?: number
  color?: string
  test_ref?: Ref<any>
}

const Scaler = forwardRef<ScalerRef, Props>((props, ref) => {
  const { value, onChange, onComplete, scale = 1, color = Color.premiumBlack, test_ref, style, ...otherProps } = props
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [endpoints, setEndpoints] = useState<Position[]>(value || [])
  const [moveEndpoints, setMoveEndpoints] = useState<Position[]>([])
  const scaler = useRef(null)
  const endpointRefs = [useRef(null), useRef(null)]
  const status =
    STATUS[
      endpoints?.filter((endpoint) => typeof endpoint.x === 'number' && typeof endpoint.y === 'number')?.length || 0
    ]

  function shouldSnapVertical(positions: Position[]) {
    return Math.abs(positions[0]?.x - positions[1]?.x) < Math.abs(positions[0]?.y - positions[1]?.y)
  }

  useEffect(() => {
    setEndpoints(value || [])
  }, [JSON.stringify(value)])

  useLayoutEffect(() => {
    const subscriptions: Subscription[] = []
    if (status !== 'edit' && scaler.current) {
      subscriptions.push(
        fromEvent<MouseEvent>(scaler.current, 'mousedown')
          .pipe(
            concatMap((start: MouseEvent) =>
              fromEvent<MouseEvent>(scaler.current!, 'mouseup').pipe(
                takeUntil(
                  fromEvent<MouseEvent>(scaler.current!, 'mousemove').pipe(
                    subscribeOn(animationFrameScheduler),
                    filter((event: MouseEvent) => {
                      const { offsetX, offsetY } = event
                      return Math.abs(offsetX - start.offsetX) > 5 || Math.abs(offsetY - start.offsetY) > 5
                    }),
                  ),
                ),
                map(() => start),
              ),
            ),
          )
          .subscribe((event: MouseEvent) => {
            const { offsetX: x, offsetY: y, shiftKey } = event
            const position = { x, y }
            if (shiftKey) {
              if (shouldSnapVertical([position, endpoints[0]])) position.x = endpoints[0].x
              else position.y = endpoints[0].y
            }
            if (status === 'start') setEndpoints([position])
            else setEndpoints([endpoints[0], position])
            setMoveEndpoints([])
            if (status === 'end') onComplete?.([endpoints[0], position])
          }),
      )
    }
    if (status === 'end' && scaler.current) {
      subscriptions.push(
        fromEvent<MouseEvent>(scaler.current, 'mousemove')
          .pipe(subscribeOn(animationFrameScheduler))
          .subscribe((event: MouseEvent) => {
            const { offsetX: x, offsetY: y, shiftKey } = event
            const position = { x, y }
            if (shiftKey) {
              if (shouldSnapVertical([position, endpoints[0]])) position.x = endpoints[0].x
              else position.y = endpoints[0].y
            }
            setMoveEndpoints([undefined as any, position])
            onChange?.([endpoints[0], position])
          }),
      )
    } else if (status === 'edit') {
      const mapMoveEvent = (startEvent: MouseEvent, endpoint: Position, otherEndpoint: Position) =>
        map((event: MouseEvent) => {
          if (event.type === 'mousemove') {
            event.preventDefault()
            event.stopPropagation()
          }
          const scale = scaleOfElement(event.target as HTMLElement)
          const position = {
            x: endpoint.x + (event.clientX - startEvent.clientX) / scale,
            y: endpoint.y + (event.clientY - startEvent.clientY) / scale,
          }

          if (event.shiftKey) {
            if (shouldSnapVertical([position, otherEndpoint])) position.x = otherEndpoint.x
            else position.y = otherEndpoint.y
          }

          return position
        })
      subscriptions.push(
        fromEvent<MouseEvent>(endpointRefs[0].current!, 'mousedown')
          .pipe(
            switchMap((startEvent) =>
              fromEvent<MouseEvent>(scaler.current!, 'mousemove').pipe(
                subscribeOn(animationFrameScheduler),
                mapMoveEvent(startEvent, endpoints[0], endpoints[1]),
                takeUntil(
                  fromEvent<MouseEvent>(scaler.current!, 'mouseup').pipe(
                    mapMoveEvent(startEvent, endpoints[0], endpoints[1]),
                    tap((position) => {
                      setEndpoints([position, endpoints[1]])
                      setMoveEndpoints([])
                      onComplete?.([position, endpoints[1]])
                    }),
                  ),
                ),
              ),
            ),
          )
          .subscribe((position) => {
            setMoveEndpoints([position, endpoints[1]])
            onChange?.([position, endpoints[1]])
          }),
      )
      subscriptions.push(
        fromEvent<MouseEvent>(endpointRefs[1].current!, 'mousedown')
          .pipe(
            switchMap((startEvent) =>
              fromEvent<MouseEvent>(scaler.current!, 'mousemove').pipe(
                subscribeOn(animationFrameScheduler),
                mapMoveEvent(startEvent, endpoints[1], endpoints[0]),
                takeUntil(
                  fromEvent<MouseEvent>(scaler.current!, 'mouseup').pipe(
                    mapMoveEvent(startEvent, endpoints[1], endpoints[0]),
                    tap((position) => {
                      setEndpoints([endpoints[0], position])
                      setMoveEndpoints([])
                      onComplete?.([endpoints[0], position])
                    }),
                  ),
                ),
              ),
            ),
          )
          .subscribe((position) => {
            setMoveEndpoints([endpoints[0], position])
            onChange?.([endpoints[0], position])
          }),
      )
    }
    return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
  }, [status, endpoints])

  useImperativeHandle(ref, () => ({
    reset: () => {
      setEndpoints([])
    },
  }))

  useImperativeHandle(test_ref, () => ({
    layout: (event: LayoutChangeEvent) => {
      layout(event)
    },
  }))

  function layout(event: LayoutChangeEvent) {
    const { width, height } = event.nativeEvent.layout
    setSize({ width, height })
  }
  return (
    <View
      ref={scaler}
      style={[endpoints?.length < 2 && styles.add, styles.container, style]}
      onLayout={layout}
      {...otherProps}
    >
      <Svg height={size.height} width={size.width} viewBox={`0 0 ${size.width} ${size.height}`}>
        <Defs>
          <Marker
            id="arrow-start"
            viewBox={`0 0 ${ARROW_WIDTH} ${ARROW_HEIGHT}`}
            refX="0"
            refY={ARROW_HEIGHT / 2}
            markerWidth={ARROW_WIDTH}
            markerHeight={ARROW_HEIGHT}
            orient="auto"
            markerUnits="strokeWidth"
          >
            <Path d={`M 0 ${ARROW_HEIGHT / 2} L ${ARROW_WIDTH} ${ARROW_HEIGHT} L ${ARROW_WIDTH} 0 z`} fill={color} />
          </Marker>
          <Marker
            id="arrow-end"
            viewBox={`0 0 ${ARROW_WIDTH} ${ARROW_HEIGHT}`}
            refX={ARROW_WIDTH}
            refY={ARROW_HEIGHT / 2}
            markerWidth={ARROW_WIDTH}
            markerHeight={ARROW_HEIGHT}
            orient="auto"
            markerUnits="strokeWidth"
          >
            <Path d={`M 0 0 L ${ARROW_WIDTH} ${ARROW_HEIGHT / 2} L 0 ${ARROW_HEIGHT} z`} fill={color} />
          </Marker>
        </Defs>
        {/* <Rect width={100} height={100} x={100} y={100} fill="#f00" stroke="none" /> */}
        {status !== 'start' && (moveEndpoints.length === 2 || endpoints.length === 2) && (
          <G
            stroke={color}
            strokeWidth={2 / scale}
            strokeLinecap="butt"
            fill="none"
            fillRule="evenodd"
            markerStart="url(#arrow-start)"
            markerEnd="url(#arrow-end)"
          >
            <Line
              x1={moveEndpoints[0]?.x || endpoints[0]?.x}
              y1={moveEndpoints[0]?.y || endpoints[0]?.y}
              x2={moveEndpoints[1]?.x || endpoints[1]?.x}
              y2={moveEndpoints[1]?.y || endpoints[1]?.y}
              strokeDasharray={status === 'end' ? '4' : undefined}
            />
          </G>
        )}
      </Svg>
      {status === 'edit' &&
        endpoints.map((endpoint, index) => (
          <View
            key={`endpoint-${index === 0}`}
            style={[
              styles.endpoint,
              {
                left: (moveEndpoints[index]?.x || endpoint.x) - ENDPOINT_HANDLE / scale / 2,
                top: (moveEndpoints[index]?.y || endpoint.y) - ENDPOINT_HANDLE / scale / 2,
                width: ENDPOINT_HANDLE / scale,
                height: ENDPOINT_HANDLE / scale,
                borderRadius: ENDPOINT_HANDLE / scale / 2,
              },
            ]}
            ref={endpointRefs[index]}
            testID={`Scaler.Endpoint${index === 0 ? 'Start' : 'End'}`}
          />
        ))}
    </View>
  )
})

export default Scaler
